import type {
  ApiListRequest,
  ApiListResponse,
  ApiListResponseV3,
} from "../@types/services/Api";
import type {
  SupplierSapData,
  Vendor,
  VendorFormData,
  VendorSapDetails,
  VendorsFilter,
  VendorsOrder,
  VendorUpdateAttributes,
} from "../@types/services/Vendor";
import type { VendorInvitation } from "../@types/services/VendorInvitation";
import { api } from "./api";

const API_BASE_URL = "/vsupplierregistration/api/v2/vendors";
const API_BASE_URL_V3 = "/api/v3";

export const blockVendor = async (
  id: Vendor["id"],
  justification?: string,
): Promise<{ message: string }> => {
  const res = await api.post<{ message: string }>(
    `/${id}/block`,
    { justification },
    { baseURL: API_BASE_URL },
  );
  return res.data;
};

export const cancelInvitation = async (
  vendorId: Vendor["id"],
  invitationId: number,
): Promise<{ message: string; id: number }> => {
  const response = await api.delete(
    `/${vendorId}/cancel_invitation/${invitationId}`,
    { baseURL: API_BASE_URL },
  );
  return response.data;
};

export const createVendor = async (data: VendorFormData): Promise<Vendor> => {
  const response = await api.post(
    "",
    { vendor: data },
    {
      baseURL: API_BASE_URL,
    },
  );
  return response.data;
};

export const deleteVendorDocument = async (
  vendorId: Vendor["id"],
  documentId: number,
): Promise<void> => {
  await api.delete(`/${vendorId}/documents/${documentId}`, {
    baseURL: API_BASE_URL,
  });
};

export const fetchSupplierSapData = async ({
  filters,
  ...rest
}: ApiListRequest<{ searchTerm?: string }> = {}): Promise<
  ApiListResponse<SupplierSapData>
> => {
  // Use mock endpoint in development
  const endpoint =
    process.env.NODE_ENV === "development"
      ? "/mock_sap_suppliers"
      : "/sap_suppliers";

  const response = await api.get<ApiListResponse<SupplierSapData>>(endpoint, {
    baseURL: API_BASE_URL,
    params: {
      ...filters,
      ...rest,
    },
  });

  return response.data;
};

export const fetchVendor = async (id: Vendor["id"]): Promise<Vendor> => {
  const response = await api.get<Vendor>(`/${id}`, {
    baseURL: API_BASE_URL,
  });

  return response.data;
};

export interface VendorCategoriesData {
  brands: Array<{ id: number; name: string }>;
  pending_brands: Array<{ id: number; name: string; request_date: string }>;
  active_rejections: Array<{
    id: number;
    material_name: string;
    rejection_date: string;
    reason: string;
    quotation_path?: string;
    negotiation_path?: string;
  }>;
  log_rejections: Array<{
    id: number;
    material_name: string;
    rejection_date: string;
    reason: string;
    quotation_path?: string;
    negotiation_path?: string;
  }>;
  accepted_categories: {
    [key: string]: Array<{ id: number; name: string }>;
  };
}

export interface RejectionRecord {
  id: number;
  material_name: string;
  rejection_date: string;
  reason: string;
  quotation_path?: string;
  negotiation_path?: string;
}

export interface PaginationInfo {
  current_page: number;
  items_per_page: number;
  total_pages: number;
}

export type PaginatedRejections = ApiListResponseV3<RejectionRecord>;

export const fetchVendorCategories = async (
  vendorId: Vendor["id"],
): Promise<VendorCategoriesData> => {
  const response = await api.get<VendorCategoriesData>(
    `/${vendorId}/categories_data`,
    {
      baseURL: API_BASE_URL,
    },
  );

  return response.data;
};

export const fetchActiveNegotiationRejections = async (
  vendorId: Vendor["id"],
  page = 1,
  itemsPerPage = 25,
): Promise<PaginatedRejections> => {
  const response = await api.get<ApiListResponseV3<RejectionRecord>>(
    `/vendors/${vendorId}/negotiation_rejections/active`,
    {
      baseURL: API_BASE_URL_V3,
      params: {
        items_per_page: itemsPerPage,
        page,
      },
    },
  );

  return response.data;
};

export const fetchLogNegotiationRejections = async (
  vendorId: Vendor["id"],
  page = 1,
  itemsPerPage = 25,
): Promise<PaginatedRejections> => {
  const response = await api.get<ApiListResponseV3<RejectionRecord>>(
    `/vendors/${vendorId}/negotiation_rejections/log`,
    {
      baseURL: API_BASE_URL_V3,
      params: {
        items_per_page: itemsPerPage,
        page,
      },
    },
  );

  return response.data;
};

export const fetchVendorSapDetails = async (
  sapCode: string,
): Promise<VendorSapDetails> => {
  // Use mock endpoint in development
  const endpoint =
    process.env.NODE_ENV === "development"
      ? "/mock_sap_supplier"
      : "/sap_supplier";

  const response = await api.get<VendorSapDetails>(endpoint, {
    baseURL: API_BASE_URL,
    params: {
      sap_code: sapCode,
    },
  });
  return response.data;
};

export const fetchVendors = async ({
  filters,
  orders,
  ...rest
}: ApiListRequest<VendorsFilter, VendorsOrder> = {}): Promise<
  ApiListResponse<Vendor>
> => {
  const response = await api.get<ApiListResponse<Vendor>>("", {
    baseURL: API_BASE_URL,
    params: {
      ...filters,
      ...orders,
      ...rest,
    },
  });

  return response.data;
};

export const inviteUser = async (
  vendorId: Vendor["id"],
  invitation: { email: string },
): Promise<{ message: string; invitation: VendorInvitation }> => {
  const response = await api.post(
    `/${vendorId}/invite_user`,
    { invitation },
    { baseURL: API_BASE_URL },
  );
  return response.data;
};

export const removeUser = async (
  vendorId: Vendor["id"],
  userId: number,
): Promise<{ message: string }> => {
  const response = await api.delete(`/${vendorId}/users/${userId}`, {
    baseURL: API_BASE_URL,
  });
  return response.data;
};

export const toggleUserPermission = async (
  vendorId: Vendor["id"],
  userId: number,
  permission: string,
  enabled: boolean,
): Promise<{ message: string }> => {
  const response = await api.patch(
    `/${vendorId}/users/${userId}/permissions`,
    { enabled, permission },
    { baseURL: API_BASE_URL },
  );
  return response.data;
};

export const addVendorSapCode = async (
  id: Vendor["id"],
  sapCode: string,
): Promise<void> => {
  await api.patch(
    `/${id}/add_sap_code`,
    {
      vendor: {
        sap_code: sapCode,
      },
    },
    {
      baseURL: API_BASE_URL,
    },
  );
};

export const toggleVendorActive = async (
  id: Vendor["id"],
  justification?: string,
): Promise<Vendor> => {
  const res = await api.patch<Vendor>(
    `/${id}/status`,
    { justification },
    { baseURL: API_BASE_URL },
  );
  return res.data;
};

export const unblockVendor = async (
  id: Vendor["id"],
  justification?: string,
): Promise<{ message: string }> => {
  const res = await api.post<{ message: string }>(
    `/${id}/unblock`,
    { justification },
    { baseURL: API_BASE_URL },
  );
  return res.data;
};

export const updateVendor = async (
  id: Vendor["id"],
  attrs: Partial<Vendor>,
): Promise<Vendor> => {
  const response = await api.patch<Vendor>(
    `/${id}`,
    { vendor: attrs },
    { baseURL: API_BASE_URL },
  );
  return response.data;
};

export const managePermissions = async (
  id: Vendor["id"],
  permValues: {
    delivery_schedule_permission?: boolean;
    material_request_form_permission?: boolean;
    material_sourcing_permission?: boolean;
    service_sourcing_permission?: boolean;
    ssu_permission?: boolean;
  },
  justification: string,
): Promise<Vendor> => {
  const response = await api.patch<Vendor>(
    `/${id}/manage_permissions`,
    {
      vendor: {
        justification,
        ...permValues,
      },
    },
    { baseURL: API_BASE_URL },
  );
  return response.data;
};

export const updateVendorPermissions = async (
  id: Vendor["id"],
  attrs: VendorUpdateAttributes,
): Promise<Vendor> => {
  const response = await api.patch<Vendor>(
    `/${id}`,
    { vendor: attrs },
    { baseURL: API_BASE_URL },
  );
  return response.data;
};

export const updateVendorProfileImage = async (
  id: Vendor["id"],
  signedId: string,
): Promise<Vendor> => {
  const payload = {
    vendor: {
      profile_image: signedId,
    },
  };

  const response = await api.patch<Vendor>(`/${id}`, payload, {
    baseURL: API_BASE_URL,
  });
  return response.data;
};

export const updateDocumentCategory = async (
  vendorId: Vendor["id"],
  docId: number,
  categoryId: number | null,
): Promise<Vendor> => {
  const response = await api.patch<Vendor>(
    `/${vendorId}/documents/categories`,
    { categories: { [docId]: categoryId } },
    { baseURL: API_BASE_URL },
  );
  return response.data;
};

export const uploadVendorDocuments = async (
  vendorId: Vendor["id"],
  documents: Array<{
    signed_id: string;
    document_category_id?: number | null;
    is_pitch?: boolean;
  }>,
): Promise<Vendor> => {
  const response = await api.post<Vendor>(
    `/${vendorId}/documents`,
    { documents },
    { baseURL: API_BASE_URL },
  );
  return response.data;
};

export const removeNegotiationRejection = async (
  vendorId: Vendor["id"],
  rejectionId: number,
): Promise<{ message: string }> => {
  const response = await api.delete<{ message: string }>(
    `/${vendorId}/negotiation_rejections/${rejectionId}`,
    { baseURL: API_BASE_URL },
  );
  return response.data;
};
